import { Injectable } from '@angular/core';
import { AccountService } from '../../user/account.service';
import { NavController } from '@ionic/angular';
import { LocalStorageService } from '../../localStorage/localStorage.service';
import { localStorageKeys } from '../../localStorage/localStorageVariables';
import { ClientService } from '../client/client.service';
import { Location, PlatformLocation } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public partnerDomain: string;
  public currentTheme: string;
  public clientParameter: string;
  public data: any;
  constructor(
    private accountService: AccountService,
    private navController: NavController,
    private localStorageData: LocalStorageService,
    private clientService: ClientService,
    private location: Location,
    private platformLocation: PlatformLocation
  ) {
    this.partnerDomain = window.location.hostname;
  }

  get defaultRoute() {
    return this.data?.defaultRoute;
  }

  get getClient() {
    return this.localStorageData.getItem(localStorageKeys.CLIENT);
  }
  get getPartner(): any {
    return JSON.parse(this.localStorageData.getItem(localStorageKeys.PARTNER));
  }
  get getAppName() {
    return this.getPartner?.appName;
  }
  get getAppLogo() {
    return this.getPartner?.appLogo;
  }
  get getAppFavicon() {
    return this.getPartner?.favicon;
  }
  get getAppKey() {
    return this.getPartner?.key;
  }
  get getAppPid() {
    return this.getPartner?.pid;
  }
  get getAppTheme() {
    return this.getPartner?.theme;
  }
  get getClientName() {
    return this.getPartner?.clientName;
  }
  get getChatSupport(){
    return this.getPartner?.chatSupport;
  }
  get getEmailSupport(){
    return this.getPartner?.emailSupport;
  }
  private static readonly FALLBACK_COUNTRY = {
    name: 'India',
    flag: '🇮🇳',
    code: 'IN',
    dialCode: '+91',
  };
  private static readonly FALLBACK_NATIONALITY = {
    iso2: 'IN',
    nationality: 'Indian',
    name:"India"
  };

  get getDefaultNationality() {
    const nationality = this.getPartner?.defaultNationality;
    if (!nationality) {
      return ThemeService.FALLBACK_NATIONALITY;
    }
    return nationality;
  }

  get getDefaultCountry() {
    const country = this.getPartner?.defaultCountry;
    if (!country) {
      return ThemeService.FALLBACK_COUNTRY;
    }
    return country;
  }

  get getDefaultCurrency(){
    return this.getPartner?.defaultCurrency || 'INR';
  }

  get getDefaultLanguage(){
    return this.getPartner?.defaultLanguage || 'en';
  }

  get getAnalyticsConfig() {
    return this.getPartner?.analytics;
  }
  set setClient(client: any) {
    this.localStorageData.setItem(localStorageKeys.CLIENT, client);
  }
  set setPartner(selectedPartner: any) {
    this.localStorageData.setItem(
      localStorageKeys.PARTNER,
      JSON.stringify(selectedPartner)
    );
  }
  set setClientData(clientData: any) {
    this.data = clientData;
  }
  get hasGuestLoginEnabled() {
    const journeyType = this.localStorageData.getItem(
      localStorageKeys.JOURNEY_TYPE
    );
    if (journeyType && journeyType.toUpperCase() === 'JSP') {
      return false;
    }
    return this.getPartner?.enableGuestLogin;
  }

  removeClient() {
    this.localStorageData.removeItem(localStorageKeys.CLIENT);
  }

  async setTheme() {
    if (this.getClientByParameter()) {
      const client = this.getClientByParameter();
      this.setClientByParameter(client);
      const isDropDownDisabled = this.localStorageData.getCookie(
        localStorageKeys.IS_DD_DISABLED
      );
      if (client && !isDropDownDisabled) {
        this.localStorageData.setCookies(
          localStorageKeys.IS_DD_DISABLED,
          'true',
          1
        );
      }
    } else if (this.getClient) {
      const client = this.getClient;
      this.setDefaultClient(client);
    } else if (!this.getClient && !this.getClientByParameter()) {
      const partner = this.getPartnerByDomain();
      this.setThemeByDomain(partner);
    }
  }

  setThemeByDomain(partner: any) {
    const setTheme = document.body;
    setTheme.setAttribute('theme', partner?.[1]?.theme);
    this.setPartner = partner?.[1];
  }

  setClientByParameter(clientName: any) {
    const client: any = this.getPartnerByDomain();
    const clientData = this.getClientData(client, clientName);
    if (clientName) {
      if (clientData.length !== 0) {
        if (this.getClient) {
          if (this.getClient !== clientName) {
            this.accountService.logout();
            this.navController.navigateRoot(['login'], {
              queryParams: { client: clientName },
            });
            this.localStorageData.setItem(localStorageKeys.CLIENT, clientName);
            this.setPartner = clientData?.[0];
          } else {
            const setTheme = document.body;
            setTheme.setAttribute('theme', clientData?.[0]?.theme);
            this.setPartner = clientData?.[0];
          }
        } else {
          this.accountService.logout();
          this.navController.navigateRoot(['login'], {
            queryParams: { client: clientName },
          });
          const setTheme = document.body;
          setTheme.setAttribute('theme', clientData?.[0]?.theme);
          this.localStorageData.setItem(localStorageKeys.CLIENT, clientName);
          this.setPartner = clientData?.[0];
        }
      } else {
        this.accountService.logout();
        this.navController.navigateRoot(['login']);
        const setTheme = document.body;
        setTheme.setAttribute('theme', client?.[1]?.theme);
        this.setPartner = client?.[1];
      }
    }
  }

  setThemeByName(name: string) {
    const selectedpartner: any = this.getPartnerByDomain();
    let selectedClient;
    if (selectedpartner) {
      if (selectedpartner?.[1].clientCode === name) {
        selectedClient = selectedpartner?.[1];
      } else if (selectedpartner?.[1].clientList.length !== 0) {
        const matchingClient = selectedpartner?.[1].clientList.find(
          (value) => value?.clientCode === name
        );
        selectedClient = matchingClient;
      }
    }
    if (selectedClient) {
      if (!selectedClient?.domain) {
        this.localStorageData.setItem(
          localStorageKeys.CLIENT,
          selectedClient?.clientName
        );
        this.setPartner = selectedClient;
      }
      const setTheme = document.body;
      setTheme.setAttribute('theme', selectedClient?.theme);
      this.setPartner = selectedClient;
    }
  }

  setDefaultClient(clientName: string) {
    const client = this.getPartnerByDomain();
    const clientData = this.getClientData(client, clientName);
    this.localStorageData.setItem(
      localStorageKeys.CLIENT,
      clientData?.[0]?.clientName
    );
    const setTheme = document.body;
    setTheme.setAttribute('theme', clientData?.[0]?.theme);
    this.setPartner = clientData?.[0];
  }

  getPartnerByDomain() {
    if (
      Object.entries(this.data?.partnerList).find((item: any) =>
        item?.[1]?.domain.includes(this.partnerDomain)
      )
    ) {
      return Object.entries(this.data?.partnerList).find((item: any) =>
        item?.[1]?.domain.includes(this.partnerDomain)
      );
    } else {
      return Object.entries(this.data?.partnerList)?.[0];
    }
  }

  getClientData(partner: any, client: string) {
    return partner?.[1]?.clientList.filter(
      (item: any) => item.clientName === client
    );
  }

  getClientByParameter() {
    const origin = location?.origin;
    const currentUrl = origin + this.location.path();
    const url = new URL(currentUrl);
    return new URLSearchParams(url.search)?.get('client');
  }
}
